import type { CardId, DealResult, Move, PlayerView, Seat, Suit } from '@ubs/rules';

/**
 * İstemci ↔ sunucu protokolü.
 *
 * İKİ KANAL, bilerek ayrı:
 *
 *  `event` → NE OLDU. Ayrık, sıralı, animasyonu tetikleyen olaylar. İstemci
 *            bunları bir kuyruğa alıp sırayla oynatıyor; ekrandaki durum
 *            sunucunun birkaç yüz ms gerisinden geliyor.
 *  `view`  → DURUM NE. Doğruluk kaynağı. Kuyruk boşalınca uygulanıyor;
 *            yeniden bağlanmada kuyruk atılıp doğrudan buna oturuluyor.
 *
 * Tek başına `view` göndermek istemciyi "kartlar neden değişti?" sorusunu
 * tersine mühendislikle çözmeye zorlardı.
 */

export interface PublicSeat {
  seat: Seat;
  name: string;
  connected: boolean;
  /** Koltuk bota devredildi mi (çıktı ya da üst üste süre aşımı). */
  bot: boolean;
  /** Bot bu koltuk adına kaç hamle oynadı — maç sonunda şeffaflık için. */
  botMoves: number;
}

export type GameEvent =
  | { kind: 'matchStart'; seats: PublicSeat[] }
  | { kind: 'dealStart'; dealIndex: number; role8: Seat }
  /** Alışveriş fazı açıldı: kim kimden kaç kart alacak (kartlar değil). */
  | { kind: 'exchangeStart'; plan: { from: Seat; to: Seat; count: number }[] }
  /** Bir alışveriş tamamlandı — kartların uçuşunu bu tetikliyor. */
  | { kind: 'exchangeStep'; from: Seat; to: Seat; count: number }
  | { kind: 'exchangeDone' }
  | { kind: 'trumpChosen'; seat: Seat; suit: Suit }
  /** 8'ci gömdü ve yerdeki kartları aldı. Kartlar gizli, yalnızca olay açık. */
  | { kind: 'buryDone'; seat: Seat }
  | { kind: 'play'; seat: Seat; card: CardId }
  | { kind: 'trickEnd'; winner: Seat; tricks: number[] }
  | { kind: 'dealEnd'; result: DealResult }
  | { kind: 'presence'; seat: Seat; connected: boolean; bot: boolean }
  | { kind: 'chat'; seat: Seat; text: string }
  | { kind: 'matchOver'; standings: { seat: Seat; score: number; place: number }[] };

/**
 * Hazır mesajların GÖMÜLÜ yedeği.
 *
 * Tek doğru kaynak değil: sunucu kendi listesini bir değişkenden okuyup katılan
 * istemciye `phrases` mesajıyla gönderiyor, böylece cümleler yeni sürüm
 * çıkmadan değiştirilebiliyor. Bu liste iki durumda kullanılıyor — sunucuda
 * liste yoksa ve istemci botlarla OFFLINE oynarken (o modda sunucu yok).
 *
 * Neden hazır liste: klavye açmadan tek dokunuşla çalışıyor (yatay telefonda
 * klavye ekranın yarısını yiyor) ve serbest metin olmadığı için moderasyon
 * derdi yok. Mesaj METİN olarak taşınıyor, indeks olarak değil: liste
 * değişebildiği için indeks sunucu ile istemcide farklı cümleye denk gelip
 * herkesin ekranında yanlış mesaj gösterebilirdi.
 */
export const FALLBACK_CHAT_PHRASES: readonly string[] = [
  'Selam beyler',
  'Koz ne oldu?',
  'Sekiz kolay gelsin. 😅',
  'Yerden ne çıktı acaba?',
  'Gömdün mü kralı?',
  'Yükselt yükselt!',
  'Kozum bitti valla.',
  'Bu el benim.',
  'Vallaha bak.',
  'Bırak la.',
  'Senin elinde ne var la!',
  'Var mı götü yiyen?',
  'Üç bile alamadın.',
  'Hep bana oynuyorsunuz.',
  'Bana uylamayın',
  'Yihh!',
  'Ver lan şunu salah',
  'Mal la bu',
  'İyi ki gelmişim la',
  'Yaz la 500 yaz. 📞',
  'Sahlep ama çay bardağında',
  'Alıyım! ☕',
  '5 çay yaz.',
  'Abi bana kola.',
];

/**
 * Tur kapandıktan sonra üç kartın masada OLDUĞU GİBİ durduğu süre.
 *
 * Süpürme ancak bundan sonra başlıyor: son kart iner inmez kartlar
 * kazanana doğru kaçınca kimin ne attığı okunamıyordu. Bekleme `trickEnd`
 * olayının süresinin İÇİNDE — yani sunucunun sıra zamanlaması da bunu
 * hesaba katıyor, bot bu bir saniyeyi kesmiyor.
 */
export const TRICK_HOLD_MS = 500;

/**
 * Bir olayın ekranda ne kadar süreceği (ms).
 * Sıra tabanlı oyunda uzun animasyon bekletiyor — kısa tutup okunur bırakıyoruz.
 *
 * Burada, protokolde duruyor çünkü İKİ TARAF da kullanıyor: istemci olayları
 * bu tempoda oynatıyor, sunucu da sıra zamanlayıcısını buna göre geciktiriyor.
 * Yalnızca istemcide dururken sunucu tempoyu bilmiyor ve bot, kartlar daha
 * dağıtılırken oynuyordu.
 */
export function eventDuration(event: GameEvent): number {
  switch (event.kind) {
    case 'play':
      // Kartın masaya oturma süresiyle hizalı. Daha kısa olursa rakibin kartı
      // inmeden sıradaki olay başlıyor.
      return 300;
    case 'trickEnd':
      // Önce duraklama (kartlar okunsun), sonra kazanana doğru süpürme.
      return TRICK_HOLD_MS + 620;
    case 'dealEnd':
      return 2200; // el sonu özeti okunacak kadar
    case 'exchangeStart':
      return 450;
    case 'exchangeStep':
      // Kartların uçuşu. Gelen kartlar zaten elde YUKARIDA duruyor, ayrıca
      // okunacak bir duyuru yok — bu yüzden kısa.
      return 900;
    case 'exchangeDone':
      // Aşamanın kapanışı için bir soluk: alışveriş bitmeden koz sorulmasın.
      return 700;
    case 'trumpChosen':
      return 700; // koz duyurusu
    case 'buryDone':
      return 500; // dört kart yere, dört kart elden
    case 'dealStart':
      return 600; // dağıtım
    case 'matchStart':
    case 'matchOver':
      // Görsel karşılığı yok; beklemek ölü zaman yaratıyor.
      return 0;
    case 'presence':
      return 0; // bilgilendirme, akışı bekletmesin
    case 'chat':
      // Sohbet kuyruğa hiç girmiyor (`useGame` araya giriyor); yine de
      // indirgeyici bütün olay türlerini karşılamak zorunda.
      return 0;
  }
}

/** Oyuncuya özel durum. `PlayerView` rakip ellerini zaten içermiyor. */
export type ViewMessage = PlayerView & {
  /** Artan sıra numarası. İstemci geç gelen eski görünümü atıyor. */
  seq: number;
  you: Seat;
  seats: PublicSeat[];
  /** Sıra sahibinin süresinin dolacağı an (epoch ms); yoksa null. */
  deadline: number | null;
  /**
   * O sürenin tamamı (ms) — geri sayım halkasının ne kadar dolu çizileceği.
   * İstemci sabit bir sayı varsayamaz: hamle süresi, koz süresi, gömme süresi
   * ve bot düşünme süresi farklı. Sunucu tek doğru kaynak.
   */
  deadlineTotal: number | null;
};

export interface LobbyMessage {
  players: { name: string; ready: boolean }[];
  needed: number;
}

/** İstemciden gelen mesajlar. */
export interface ClientMessages {
  move: Move;
  trump: { suit: Suit };
  bury: { cards: CardId[] };
  exchange: { gives: { to: Seat; cards: CardId[] }[] };
  /**
   * Hazır mesaj metni. Sunucu kendi listesinde var mı diye bakıyor —
   * yani hâlâ beyaz liste, serbest metin kabul edilmiyor.
   */
  chat: { text: string };
  /** Dinleyiciler bağlandı; masanın durumunu ve hazır mesaj listesini iste. */
  hello: Record<string, never>;
}

/** Sunucunun yürürlükteki hazır mesaj listesi — katılışta gönderiliyor. */
export interface PhrasesMessage {
  phrases: string[];
}

export * from './derive.js';
